import { Link } from "react-router-dom";
import type { Article } from "@/types/news";
import { formatViews } from "@/utils/format";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "compact";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <article className="group">
        <Link to={`/article/${article.id}`} className="flex gap-3">
          <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
            <img
              src={article.cover}
              alt={`${article.title} - ${article.categoryName}配图`}
              title={`${article.title} - 诱惑吃瓜`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
          </div>
          <div className="flex min-w-0 flex-col justify-between py-0.5">
            <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
              {article.title}
            </h3>
            <p className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
              <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-900">
                {article.categoryName}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" aria-hidden="true" />
                {formatViews(article.views)}
              </span>
            </p>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/article/${article.id}`} className="block">
        <div className="relative h-44 w-full overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} - ${article.categoryName}配图`}
            title={`${article.title} - 诱惑吃瓜`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3 rounded-full bg-background-50/90 px-2.5 py-1 text-xs font-medium text-primary-700 backdrop-blur-sm">
            {article.categoryName}
          </span>
          {article.hot && (
            <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
              <i className="ri-fire-line" aria-hidden="true" />
              高能
            </span>
          )}
        </div>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="text-base font-semibold leading-snug text-foreground-900">
          <Link to={`/article/${article.id}`} className="transition-colors hover:text-primary-600">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.excerpt}
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line" aria-hidden="true" />
            {article.author}
          </span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true" />
              {formatViews(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true" />
              {article.comments}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}